import { APP_ROUTES } from "../constants";

/**
 * Bounded post-auth destinations. Anything else is treated as transactions
 * (never an open redirect).
 */
export type AuthResume =
  | { kind: "transfer" }
  | { kind: "transactions" }
  | { kind: "receipt"; operationId: string }
  | { kind: "cash-deposit"; operationId?: string }
  | { kind: "ofac"; operationId: string };

export type ConsumerOccupancy =
  | "acquired"
  | "already_occupant"
  | "other_occupant";

/** Bind 403: this Clerk subject cannot fulfill; artefact stays takeable. */
export const BIND_SUBJECT_BLOCKED_CODE = "USER_BLOCKED";

export function subjectBlockedTerminalState(t: {
  (key: string): string;
}): {
  title: string;
  subTitle: null;
  message: string;
  btnText: string;
  isOut: false;
  pageUrl: string;
} {
  return {
    title: t("error.cannotContinueTitle"),
    subTitle: null,
    message: t("error.cannotContinueMessage"),
    btnText: t("common.needHelp"),
    isOut: false,
    pageUrl: APP_ROUTES.SUPPORT_PAGE,
  };
}

/** Sender blocked / origination cancelled — cobro no disponible + support. */
export function originatorClosedTerminalState(t: {
  (key: string): string;
}): {
  title: string;
  subTitle: null;
  message: string;
  btnText: string;
  isOut: false;
  pageUrl: string;
} {
  return {
    title: t("error.claimUnavailableTitle"),
    subTitle: null,
    message: t("error.claimUnavailableMessage"),
    btnText: t("common.needHelp"),
    isOut: false,
    pageUrl: APP_ROUTES.SUPPORT_PAGE,
  };
}

const SAFE_ID = /^[A-Za-z0-9_-]{1,128}$/;

export function serializeAuthResume(resume: AuthResume): string {
  switch (resume.kind) {
    case "transfer":
      return "transfer";
    case "transactions":
      return "transactions";
    case "receipt":
      return `receipt:${resume.operationId}`;
    case "cash-deposit":
      return resume.operationId
        ? `cash-deposit:${resume.operationId}`
        : "cash-deposit";
    case "ofac":
      return `ofac:${resume.operationId}`;
  }
}

/**
 * Parse `?resume=`. Missing → transfer (default fulfill path). Invalid → transactions.
 */
export function parseAuthResume(raw: string | null | undefined): AuthResume {
  if (raw == null || raw === "") {
    return { kind: "transfer" };
  }
  if (raw === "transfer") return { kind: "transfer" };
  if (raw === "transactions") return { kind: "transactions" };
  if (raw === "cash-deposit") return { kind: "cash-deposit" };

  const prefixed = (prefix: string): string | null => {
    if (!raw.startsWith(prefix)) return null;
    const id = raw.slice(prefix.length);
    return SAFE_ID.test(id) ? id : null;
  };

  const receiptId = prefixed("receipt:");
  if (receiptId) return { kind: "receipt", operationId: receiptId };

  const cashId = prefixed("cash-deposit:");
  if (cashId) return { kind: "cash-deposit", operationId: cashId };

  const ofacId = prefixed("ofac:");
  if (ofacId) return { kind: "ofac", operationId: ofacId };

  return { kind: "transactions" };
}

/** Query flag set after clearing a stuck pending Clerk session (one-shot). */
export const AUTH_RESET_PARAM = "authReset";

/**
 * Query flag set after dropping a Clerk session whose bind failed (one-shot).
 * The form shows the bind error instead of a silent reset.
 */
export const AUTH_ERROR_PARAM = "authError";
export const AUTH_ERROR_BIND = "bind";

export function isAuthBindErrorReturn(raw: string | null | undefined): boolean {
  return raw === AUTH_ERROR_BIND;
}

/** Set when auth is opened from the claim entry (Pay / Later / Receive). */
export const AUTH_FROM_PARAM = "from";
export const AUTH_FROM_CLAIM = "claim";

export function isAuthFromClaim(raw: string | null | undefined): boolean {
  return raw === AUTH_FROM_CLAIM;
}

export function authPagePath(
  resume: AuthResume,
  options?: { fromClaim?: boolean },
): string {
  const params = new URLSearchParams();
  params.set("resume", serializeAuthResume(resume));
  if (options?.fromClaim) params.set(AUTH_FROM_PARAM, AUTH_FROM_CLAIM);
  return `${APP_ROUTES.AUTH_PAGE}?${params.toString()}`;
}

/**
 * Absolute `/auth-page` URL that always carries `linkId` + `resume`.
 * Empty `resumeRaw` serializes as transfer (default fulfill path).
 * Used by email `setActive` and SSO `redirectUrlComplete` so Clerk cannot
 * fall through to Dashboard After sign-in / Home.
 */
export function buildAuthPageUrl(args: {
  origin: string;
  linkId?: string | null;
  resumeRaw?: string | null;
  reset?: boolean;
  fromClaim?: boolean;
  bindError?: boolean;
}): string {
  const params = new URLSearchParams();
  if (args.linkId) params.set("linkId", args.linkId);
  params.set(
    "resume",
    args.resumeRaw || serializeAuthResume(parseAuthResume(args.resumeRaw))
  );
  if (args.reset) params.set(AUTH_RESET_PARAM, "1");
  if (args.fromClaim) params.set(AUTH_FROM_PARAM, AUTH_FROM_CLAIM);
  if (args.bindError) params.set(AUTH_ERROR_PARAM, AUTH_ERROR_BIND);
  return `${args.origin}${APP_ROUTES.AUTH_PAGE}?${params.toString()}`;
}

/**
 * IdP round-trip URL. `from=claim` rides along so a cancelled SSO can
 * restore the auth back button, not only linkId and resume.
 */
export function ssoCallbackUrl(args: {
  origin: string;
  linkId?: string | null;
  resumeRaw?: string | null;
  fromClaim?: boolean;
}): string {
  const params = new URLSearchParams();
  if (args.linkId) params.set("linkId", args.linkId);
  if (args.resumeRaw) params.set("resume", args.resumeRaw);
  if (args.fromClaim) params.set(AUTH_FROM_PARAM, AUTH_FROM_CLAIM);
  const query = params.toString();
  return `${args.origin}${APP_ROUTES.SSO_CALLBACK_PAGE}${query ? `?${query}` : ""}`;
}

/**
 * In-widget auth URL. Clerk uses this instead of the Account Portal when an
 * SSO attempt is cancelled or cannot finish.
 * `search` wins for linkId; `linkIdFallback` covers routes that dropped the query.
 */
export function consumerClerkSignInUrl(args: {
  origin: string;
  search?: string | null;
  linkIdFallback?: string | null;
}): string {
  const raw = args.search ?? "";
  const params = new URLSearchParams(raw.startsWith("?") ? raw.slice(1) : raw);
  return buildAuthPageUrl({
    origin: args.origin,
    linkId: params.get("linkId") || args.linkIdFallback,
    resumeRaw: params.get("resume"),
    fromClaim: isAuthFromClaim(params.get(AUTH_FROM_PARAM)),
  });
}

export function appendResumeQuery(
  pathWithOptionalQuery: string,
  resume: string | null
): string {
  if (!resume) return pathWithOptionalQuery;
  const connector = pathWithOptionalQuery.includes("?") ? "&" : "?";
  return `${pathWithOptionalQuery}${connector}resume=${encodeURIComponent(resume)}`;
}

export function resumeToAppPath(
  resume: AuthResume,
  routes: {
    transfer: string;
    transactions: string;
    receipt: string;
    cashDeposit: string;
    ofac?: string;
    unavailable?: string;
  }
): string {
  switch (resume.kind) {
    case "transfer":
      return routes.transfer;
    case "transactions":
      return routes.transactions;
    case "receipt":
      return `${routes.receipt}/${resume.operationId}`;
    case "cash-deposit":
      return routes.transactions;
    case "ofac":
      return routes.ofac
        ? `${routes.ofac}/${resume.operationId}`
        : routes.transactions;
  }
}

const DEFAULT_RESUME_ROUTES = {
  transfer: APP_ROUTES.TRANSFER_PAGE,
  transactions: APP_ROUTES.TRANSACTIONS_PAGE,
  receipt: APP_ROUTES.RECEIPT_PAGE,
  cashDeposit: APP_ROUTES.TRANSACTIONS_PAGE,
  ofac: APP_ROUTES.OFAC_DISAMBIGUATION_PAGE,
  unavailable: APP_ROUTES.CLAIM_BLOCKED_PAGE,
};

/**
 * Post-auth destination for intention CTAs (Accept / Later / Reject / Go to app).
 * Accept (`transfer`) only reaches Transfer when this viewer can still take the
 * intention; otherwise Unavailable. Later / Reject / Go to app stay on Home.
 */
export function resolvePostAuthDestination(args: {
  resume: AuthResume;
  occupancy: ConsumerOccupancy;
  takeable: boolean;
}): string {
  const { resume, occupancy, takeable } = args;

  if (resume.kind === "transfer") {
    const canFulfill = occupancy !== "other_occupant" && takeable;
    return canFulfill
      ? APP_ROUTES.TRANSFER_PAGE
      : DEFAULT_RESUME_ROUTES.unavailable;
  }

  return resumeToAppPath(resume, DEFAULT_RESUME_ROUTES);
}
